import PetsIcon from "@mui/icons-material/Pets";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import { t } from "i18next";
import { useMemo, useState } from "react";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import { getUserImportStatus } from "../utils/importUsersHelpers";
import UserDetailsDialog from "./UserDetailsDialog";
import ImportStatusChip from "./ImportStatusChip";

export default function UserImportTable({
  users,
  excludedIds,
  onToggleUser,
  onUpdateUser,
  removeAll,
  addAllUsers,
  conflicts = {},
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detailsUserId, setDetailsUserId] = useState(null);
  const detailsUser = users.find((u) => u.id === detailsUserId) ?? null;

  const paginatedUsers = users.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const statusCounts = useMemo(() => {
    const counts = { conflict: 0, missing: 0, updated: 0, ready: 0 };
    users.forEach((user) => {
      counts[getUserImportStatus(user, conflicts[user.id])] += 1;
    });
    return counts;
  }, [users, conflicts]);

  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ px: 3, py: 2, bgcolor: "action.hover" }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {t("OverviewOfImportedUsers")}
        </Typography>
        <Box>
          <Button onClick={() => addAllUsers()}>{t("AddAll")}</Button>
          <Button onClick={() => removeAll()} color="error">
            {t("RemoveAll")}
          </Button>
        </Box>
      </Stack>

      <TableContainer>
        <Table
          sx={{
            "& .MuiTableCell-root": { py: 1.75, px: 2.5 },
            "& .MuiTableCell-head": {
              py: 1.5,
              fontWeight: 700,
              letterSpacing: 0.3,
              color: "text.secondary",
              textTransform: "uppercase",
              fontSize: "0.72rem",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>{t("FullName")}</TableCell>
              <TableCell>{t("Email")}</TableCell>
              <TableCell>{t("OIB")}</TableCell>
              <TableCell>{t("Address")}</TableCell>
              <TableCell align="center">{t("PetsPlural")}</TableCell>
              <TableCell>{t("Status")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => {
              const included = !excludedIds.has(user.id);
              const status = getUserImportStatus(user, conflicts[user.id]);
              const displayName = parseName(user.name);

              return (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => setDetailsUserId(user.id)}
                  sx={{
                    cursor: "pointer",
                    opacity: included ? 1 : 0.5,
                    transition: "background-color 0.15s ease",
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={included}
                      onChange={() => onToggleUser(user.id)}
                    />
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="body2" fontWeight={500}>
                        {displayName}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <EllipsisText value={user?.email} />
                  </TableCell>

                  <TableCell>{user.oib || "N/A"}</TableCell>

                  <TableCell>
                    <EllipsisText value={user.address} maxWidth={220} />
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      size="small"
                      icon={<PetsIcon fontSize="small" />}
                      label={user.pets.length}
                    />
                  </TableCell>

                  <TableCell>
                    <ImportStatusChip status={status} />
                  </TableCell>
                </TableRow>
              );
            })}

            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography
                    align="center"
                    color="text.secondary"
                    sx={{ py: 5 }}
                  >
                    {t("NoUsersToShow")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={users.length}
        page={page}
        labelRowsPerPage={t("RowsPerPage")}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ px: 1 }}
        backIconButtonProps={{
          title: t("PreviousPage"),
          "aria-label": t("PreviousPage"),
        }}
        nextIconButtonProps={{
          title: t("NextPage"),
          "aria-label": t("NextPage"),
        }}
      />

      <UserDetailsDialog
        user={detailsUser}
        conflict={detailsUser ? conflicts[detailsUser.id] : null}
        open={Boolean(detailsUser)}
        onClose={() => setDetailsUserId(null)}
        onUpdateUser={onUpdateUser}
      />
    </Paper>
  );
}

function EllipsisText({ value, maxWidth = 180 }) {
  if (!value) {
    return (
      <Typography variant="body2" color="text.secondary">
        N/A
      </Typography>
    );
  }

  return (
    <Tooltip title={value}>
      <Box
        sx={{
          maxWidth,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <Typography variant="body2" component="span">
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export function parseName(name) {
  return String(name)
    .split(" ")
    .map((val) => {
      return capitalizeFirstLetter(val);
    })
    .join(" ");
}
